import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap } from 'lucide-react';
import { useSubscription } from '../lib/hooks/useSubscription';
import { Link } from 'react-router-dom';

interface FeatureGateProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const FeatureGate: React.FC<FeatureGateProps> = ({ 
  feature, 
  children,
  fallback
}) => {
  const { checkFeatureAccess, currentSubscription } = useSubscription();
  const hasAccess = checkFeatureAccess(feature);

  if (hasAccess) {
    return <>{children}</>;
  }

  // If a fallback is provided, render it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Otherwise, render the default locked feature message
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-50 dark:bg-dark-800/50 rounded-lg p-6 text-center"
    >
      <div className="mx-auto w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-4">
        <Lock className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        Premium Feature
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
        This feature is available on the {getRequiredPlan(feature)} plan or higher.
        Upgrade your subscription to unlock this feature.
      </p>
      <Link
        to="/subscription"
        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        <Zap className="h-4 w-4 mr-2" />
        Upgrade Now
      </Link>
    </motion.div>
  );
};

// Helper function to determine which plan is required for a feature
function getRequiredPlan(feature: string): string {
  const chargedFeatures = [
    'advanced_analytics',
    'ai_tools',
    'advanced_scheduling'
  ];
  
  const superchargedFeatures = [
    'unlimited_storage',
    'unlimited_social',
    'priority_support',
    'custom_branding',
    'team_collaboration',
    'advanced_automation'
  ];
  
  if (superchargedFeatures.includes(feature)) {
    return 'Supercharged';
  }
  
  if (chargedFeatures.includes(feature)) {
    return 'Charged';
  }
  
  return 'Charged';
}

export default FeatureGate;